/**
 * Which brand this page is being served as, decided by the hostname.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * One deployment serves several brands: `luvel.watanfd.com` is Luvelle,
 * `taheel.watanfd.com` is Taheel, and both are CNAMEs onto the same Render
 * service. The document head, though, is baked into a single `index.html` — so
 * a Taheel customer opening an SMS'd confirm-location link got a browser tab
 * reading "Luvelle Home Spa" and a description advertising a women's luxury spa
 * (2026-08-18, reported by Islam).
 *
 * ── What the brand now decides (2026-09-15) ───────────────────────────────
 * Originally this file only fixed the document head. It now also decides:
 *
 *   1. `surface` — how much of the app this host is allowed to show.
 *      Luvelle is the FULL consumer web app (catalog, booking wizard, account).
 *      Taheel is `transactional`: the host exists ONLY to serve the links the
 *      Taheel mobile app and the operator SMS point at — pay, pay/return,
 *      confirm-location, legal. Everything else redirects to a small branded
 *      "open the app" page. Taheel has no web catalog, no web login, no web
 *      bookings list, and shipping Luvelle's would be both wrong content and a
 *      dead end (those pages call the Luvelle catalog).
 *
 *   2. The palette, via `data-brand` on <html> — see the `[data-brand='taheel']`
 *      token block in `styles/globals.css`. Values come from the Flutter app's
 *      `lib/config/taheel_config.dart` so web and app agree on the brand green.
 *
 *   3. The logo, the Apple Pay sheet name, and the voice of the shared payment
 *      copy: Luvelle's strings address a woman ("أكملتِ", "حجزك... ستديرين"),
 *      which is Luvelle's brand voice and wrong for Taheel — Taheel serves men
 *      and women. See `brand-voice.ts`.
 *
 * ── Why at runtime rather than per-brand builds ───────────────────────────
 * A build per brand means a Render service per brand, a deploy per brand, and
 * two chances to ship one of them stale. Reading the hostname costs nothing and
 * keeps one deployment.
 *
 * ── Fail-safe direction ───────────────────────────────────────────────────
 * An unknown host resolves to LUVELLE, unchanged. That direction matters: this
 * runs on the live consumer site, and the rule here is that mobile/brand work
 * must never regress the Luvelle landing. A host we do not recognise therefore
 * behaves exactly as it did before this file existed.
 */

export type BrandKey = 'luvelle' | 'taheel' | 'wafi' | 'fitness' | 'jalesaa';

/**
 * How much of the app a host may render.
 *   - `full`          the whole consumer web app (Luvelle).
 *   - `transactional` only the flows a customer is SENT to by SMS or by the
 *                     mobile app's payment WebView (Taheel, Wafi).
 */
export type BrandSurface = 'full' | 'transactional';

export interface BrandIdentity {
  key: BrandKey;
  /** Document title. Arabic first, matching the existing convention. */
  title: string;
  /** <meta name="description">. */
  description: string;
  /**
   * Favicon path under `public/`, or `null` to leave whatever `index.html`
   * shipped with. `iconType` must match the file — `index.html` declares
   * `image/svg+xml` for Luvelle's mark, and leaving that on a PNG makes some
   * browsers ignore it.
   */
  iconHref: string | null;
  iconType: string | null;
  /** `<meta name="theme-color">` — the browser chrome tint on Android/iOS. */
  themeColor: string;
  /** How much of the app this host serves. */
  surface: BrandSurface;
  /** Brand name for UI copy, per language. */
  nameAr: string;
  nameEn: string;
  /**
   * Name shown on the Apple Pay sheet (`wpwlOptions.applePay.displayName`).
   * The customer reads this while authorising a charge, so it must be the
   * brand they think they are buying from.
   */
  applePayDisplayName: string;
  /** Standalone logo file under `public/`, or null to use the React lockup. */
  logoHref: string | null;
  /**
   * The logo file's intrinsic pixel size, so the browser reserves the right box
   * before it loads. Taheel's mark is square (164×166); Wafi's is a 2:1 lockup
   * (200×100), and hardcoding one brand's numbers for the other is a visible
   * layout shift on a slow connection. Null when `logoHref` is null.
   */
  logoWidth: number | null;
  logoHeight: number | null;
}

/*
 * No store URLs here, on purpose (Islam, 2026-09-15). The brand gate page names
 * the app but does not link to a listing: everyone who reaches this page came
 * from a link WE sent them, so they already have the app or are being helped by
 * the person who sent it. The store link that DOES matter is on the deep-link
 * interstitial, which is backend-side (`b2c-dynamic-links.service.ts`) — that is
 * the surface someone lands on without the app installed.
 */

const LUVELLE: BrandIdentity = {
  key: 'luvelle',
  title: 'لوفيل · Luvelle Home Spa',
  description:
    'لوفيل — منصة حجز سبا منزلي فاخر للسيدات في المملكة العربية السعودية. احجزي تجربة عافية كاملة الراحة.',
  // Left alone: index.html already ships Luvelle's icon.
  iconHref: null,
  iconType: null,
  // Matches the `<meta name="theme-color">` index.html already ships (plum).
  themeColor: '#592159',
  surface: 'full',
  nameAr: 'لوفيل',
  nameEn: 'Luvelle',
  applePayDisplayName: 'Luvelle Spa',
  // Luvelle renders the React lockup (mark + serif wordmark), not a file.
  logoHref: null,
  logoWidth: null,
  logoHeight: null,
};

const TAHEEL: BrandIdentity = {
  key: 'taheel',
  title: 'تأهيل · Taheel',
  // Factual, and deliberately not gendered — Taheel serves men and women, and
  // the copy it inherited did not.
  description:
    'تأهيل — خدمات التأهيل: النطق والتخاطب، تعديل السلوك وتنمية المهارات، صعوبات التعلم، والجلسات الأونلاين.',
  // Supplied by Islam 2026-08-18 as a 1024px app icon; resized to 192 for a tab
  // (9KB instead of 38KB — a favicon does not need app-store resolution).
  iconHref: '/taheel-icon.png',
  iconType: 'image/png',
  // Primary/Primary from the Flutter theme (taheel_config.dart primaryColor).
  themeColor: '#92B35B',
  // The whole point of this host: payment + location-confirm, nothing else.
  surface: 'transactional',
  nameAr: 'تأهيل',
  nameEn: 'Taheel',
  applePayDisplayName: 'Taheel',
  // Copied verbatim from the Flutter app (assets/taheel/images/logo.svg) so the
  // web mark and the app mark are the same file, not a redraw. 2.3KB.
  logoHref: '/taheel-logo.svg',
  logoWidth: 164,
  logoHeight: 166,
};

const WAFI: BrandIdentity = {
  key: 'wafi',
  title: 'وافي · Wafi',
  // Deliberately not gendered, for the same reason Taheel's is not: Wafi serves
  // households, and the copy this file's default would have given it was
  // written for a women's luxury spa.
  description:
    'وافي — خدمات منزلية بالساعة وخدمات العمالة المقيمة في المملكة العربية السعودية.',
  // From the Wafi Flutter app's iOS AppIcon (1024/512.png), resized to 192 for
  // a tab — 7KB instead of 32KB, and a favicon does not need app-store
  // resolution. Same treatment `/taheel-icon.png` got.
  iconHref: '/wafi-icon.png',
  // index.html declares image/svg+xml; a PNG left under that type is dropped by
  // some browsers instead of rendered.
  iconType: 'image/png',
  // The deep blue the brand gradient ends on (#d7d657 → #2baab7 → #0c6094),
  // read out of the logo itself rather than guessed. Matches the
  // `[data-brand='wafi']` token block in styles/globals.css.
  themeColor: '#0c6094',
  // The whole point of this host: payment + location-confirm, nothing else.
  surface: 'transactional',
  nameAr: 'وافي',
  nameEn: 'Wafi',
  applePayDisplayName: 'Wafi',
  // `assets/images/full_logo.svg` from the Wafi Flutter app, verbatim, so the
  // web mark is the same file as the app mark rather than a redraw. Its
  // gradient and its #032333 wordmark are baked in, which is why BrandMark
  // renders it as an `<img>`: it must NOT inherit `currentColor` the way the
  // Luvelle lockup does, or it would turn white on a gradient surface.
  logoHref: '/wafi-logo.svg',
  logoWidth: 200,
  logoHeight: 100,
};

/*
 * ── Jalesaa (2026-10-13, Islam) ──────────────────────────────────────────
 * A Luvelle-like brand: its own app and BFF (`watanhr-jalesaa-api`), and a
 * landing that behaves like Taheel's — transactional only (pay, pay/return,
 * confirm-location, legal). Nothing else is served on its hosts.
 *
 * One spelling everywhere (Islam, 2026-10-13): key, API segment
 * (`/mobile/jalesaa/*`), service and host names
 * (`watanhr-jalesaa-landing-*.watansolutions.com`) are all `jalesaa`; the
 * backend uses the same spelling.
 *
 * ⚠️ PLACEHOLDERS until the Jalesaa brand guide arrives: no logo file (the
 * BrandMark renders the name), no favicon (the tab keeps index.html's icon),
 * and the theme colour is Luvelle's plum — there is no `[data-brand='jalesaa']`
 * token block yet, so the palette inherits `:root`. The copy below is generic
 * on purpose and must be replaced by the brand's own wording.
 */
const JALESAA: BrandIdentity = {
  key: 'jalesaa',
  title: 'جليسة · Jalesaa',
  // Not gendered, and deliberately generic until the brand's own copy exists.
  description: 'جليسة — خدمات منزلية بالساعة في المملكة العربية السعودية.',
  iconHref: null,
  iconType: null,
  themeColor: '#592159',
  surface: 'transactional',
  nameAr: 'جليسة',
  nameEn: 'Jalesaa',
  applePayDisplayName: 'Jalesaa',
  logoHref: null,
  logoWidth: null,
  logoHeight: null,
};

/**
 * Hostname → brand. Exact matches, not a substring test: `taheel` or `wafi`
 * appearing inside some other hostname must not silently rebrand a page.
 *
 * A map rather than one Set per brand: the third brand is where "add another
 * Set and another ternary" starts going wrong, and the lookup below has to stay
 * a single exact match for the guard tests to keep meaning what they say.
 */
/**
 * Fitness (2026-10-13) — a like-for-like copy of Taheel under its own brand:
 * same transactional surface, same page shapes. Everything visual here is
 * Taheel's as a PLACEHOLDER (theme colour, logo, icon) until the Fitness
 * identity is handed over; the copy and the hosts are its own.
 */
const FITNESS: BrandIdentity = {
  key: 'fitness',
  title: 'فيتنس · Fitness',
  description:
    'فيتنس — جلسات لياقة وتدريب شخصي في المملكة العربية السعودية، بنفس تجربة الحجز والدفع.',
  iconHref: '/fitness-icon.png',
  iconType: 'image/png',
  themeColor: '#92B35B',
  surface: 'transactional',
  nameAr: 'فيتنس',
  nameEn: 'Fitness',
  applePayDisplayName: 'Fitness',
  logoHref: '/fitness-logo.svg',
  logoWidth: 164,
  logoHeight: 166,
};

const HOSTS: ReadonlyMap<string, BrandIdentity> = new Map([
  ['taheel.watanfd.com', TAHEEL],
  ['taheel.watanfirsthr.com', TAHEEL],
  // ⚠️ Assumed, following Taheel's naming. Confirm before the DNS is created —
  // a host that is not in this map renders as LUVELLE, which on a Wafi payment
  // link is a plum women's-spa page.
  // ⚠️ `wfi2`, NOT `wafi` — and the mismatch is deliberate (Islam, 2026-09-17).
  //
  // The brand's PUBLIC identity — the existing domain and the app-store name —
  // is `wfi`. The internal key everywhere in code and data is `wafi`. Both are
  // correct, in their own layer, and confusing them is the documented trap:
  // `app-context.guard.ts` rejects an unknown app slug, and the scalar
  // app-scope filter returns a plausible EMPTY list for one.
  //
  // The `2` distinguishes this from the existing wfi property, following
  // `api2.watanfirsthr.com` — a number rather than "new", which stops being
  // true and stays in the URL.
  ['wfi2.watanfd.com', WAFI],
  ['wfi2.watanfirsthr.com', WAFI],
  // Fitness and Jalesaa: no production host decided yet. Add each one here the
  // day it is. Their dev and staging hosts are NOT listed here, the same as
  // Taheel's and Wafi's: every app has one host per environment, and the dev
  // and staging ones arrive through that environment's VITE_BRAND_HOSTS
  // (Islam, 2026-10-14). Until they are configured there, those hosts render
  // as Luvelle.
]);

/** The brand for a hostname. Unknown ⇒ Luvelle, unchanged. */
export function resolveBrandFromHost(host: string | undefined | null): BrandIdentity {
  const h = (host ?? '').trim().toLowerCase().split(':')[0];
  return HOSTS.get(h) ?? LUVELLE;
}

/** Hosts where a `?brand=` override is honoured — dev machines only. */
function isLocalHost(host: string): boolean {
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]' ||
    host.endsWith('.local')
  );
}

/**
 * The brand for a full location (host + query), honouring `?brand=taheel` ONLY
 * on a dev host.
 *
 * Why the host restriction: the override exists so Islam can see a brand's
 * surface at `localhost:5173/?brand=taheel` (or `?brand=wafi`) without editing
 * /etc/hosts. Left
 * unrestricted it would be a link anyone could send that repaints the live
 * Luvelle site — a brand regression reachable from a URL. Dev-only keeps the
 * convenience and removes the exposure.
 */
export function resolveBrand(
  host: string | undefined | null,
  search = '',
): BrandIdentity {
  const h = (host ?? '').trim().toLowerCase().split(':')[0];
  if (isLocalHost(h)) {
    const override = new URLSearchParams(search).get('brand')?.toLowerCase();
    if (override === 'taheel') return TAHEEL;
    if (override === 'wafi') return WAFI;
    if (override === 'fitness') return FITNESS;
    if (override === 'jalesaa') return JALESAA;
    if (override === 'luvelle') return LUVELLE;
  }
  return resolveBrandFromHost(host);
}

/**
 * The resolved brand for THIS page load, frozen at module init.
 *
 * Frozen deliberately: the brand is a property of the hostname, which cannot
 * change without a full navigation, and a stable value means components can
 * read it without a context/provider and without re-render churn.
 */
export const brand: BrandIdentity =
  typeof window === 'undefined'
    ? LUVELLE
    : resolveBrand(window.location.hostname, window.location.search);

/** True when this host serves only the payment / location-confirm flows. */
export const isTransactionalBrand = brand.surface === 'transactional';

/**
 * The API path segment for the brand this page is being served as.
 *
 * ── Why this exists (2026-09-06, Hyperpay per-application isolation) ───────
 * The backend derives a contract's `brand` from the ROUTE CLASS it was created
 * through: `/mobile/luvelle/hourly/bookings` is served by a controller whose
 * `bookingBrand` is the literal `'luvelle'`, and `/mobile/taheel/...` by one
 * whose seam says `'taheel'`. No header changes that — the route IS the claim.
 *
 * This app serves BOTH brands from one bundle (see TAHEEL_HOSTS above), and
 * every call was hardcoded to the `luvelle` segment. So a booking made on the
 * Taheel host was stamped `brand = 'luvelle'`.
 *
 * That was already wrong for reporting. Once a brand has its own Hyperpay
 * merchant account it becomes a MONEY bug: the checkout's credentials, its
 * pay-page host and its refund destination are all chosen from
 * `contract.brand`, so a Taheel customer's payment would settle into Luvelle's
 * merchant account.
 *
 * Use this ONLY for routes that exist under both namespaces. Several do not
 * (`wallet`, `profile`, `addresses`, `lookups` are Luvelle-only today), and
 * those are safe on the shared path because their brand comes from the
 * customer row or from nothing at all — not from the route.
 */
export function apiBrandSegment(host?: string): BrandKey {
  // Guarded rather than defaulting to `window.location.hostname` in the
  // signature: this module is imported by API helpers that also run outside a
  // DOM (tests, and any SSR/prerender step), where touching `window` throws
  // before the brand is even needed. No window ⇒ no host ⇒ Luvelle, which is
  // the same fail-safe direction resolveBrandFromHost already takes.
  const resolved =
    host ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  return resolveBrandFromHost(resolved).key;
}

/**
 * Write the resolved brand into the document head. Called before React paints,
 * beside `applyDirOnLoad`, so the tab is right on the first frame rather than
 * flickering from one brand to another.
 */
export function applyBrandOnLoad(host: string = window.location.hostname): void {
  const resolved = resolveBrand(host, window.location.search);
  // Drives the `[data-brand='taheel']` palette block in globals.css. Set here,
  // before React mounts, so the first frame is already the right colour.
  document.documentElement.dataset.brand = resolved.key;
  document.title = resolved.title;

  const desc = document.querySelector('meta[name="description"]');
  if (desc) desc.setAttribute('content', resolved.description);

  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', resolved.themeColor);

  if (resolved.iconHref) {
    const icon = document.querySelector('link[rel="icon"]');
    if (icon) {
      icon.setAttribute('href', resolved.iconHref);
      // Swapped together: index.html declares image/svg+xml, and leaving that
      // on a PNG makes some browsers drop the icon rather than render it.
      if (resolved.iconType) icon.setAttribute('type', resolved.iconType);
    }
  }
}
